// Per-device scan feedback: 'beep' (default), 'voice' (says "item scanned"),
// 'vibrate' (buzz only — only where the Vibration API exists), or 'off'.
// Used by the camera scanner across the app (Transfers, Billing, Add Stock).
const KEY = 'sc_scan_feedback';

// All modes in display order. 'vibrate' is only *offered* where supported.
export const SCAN_FEEDBACK_ALL = ['beep', 'voice', 'vibrate', 'off'];

// --- capability detection -------------------------------------------------
export const isVibrateSupported = () =>
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

export const isAudioSupported = () =>
  typeof window !== 'undefined' && !!(window.AudioContext || window.webkitAudioContext);

export const isSpeechSupported = () =>
  typeof window !== 'undefined' && 'speechSynthesis' in window;

// Modes that can actually do something on THIS device.
export function availableFeedbackOptions() {
  return SCAN_FEEDBACK_ALL.filter((o) => (o === 'vibrate' ? isVibrateSupported() : true));
}

// --- preference storage ---------------------------------------------------
export const getScanFeedback = () => {
  try {
    const v = localStorage.getItem(KEY);
    return SCAN_FEEDBACK_ALL.includes(v) ? v : 'beep';
  } catch (e) { return 'beep'; }
};
export const setScanFeedback = (v) => {
  try { localStorage.setItem(KEY, v); } catch (e) { /* ignore */ }
};

/**
 * Returns the effective feedback mode for this device.
 * If the saved preference can't work here (e.g. 'vibrate' on iOS Safari), it
 * returns a supported fallback and flags `changed` so the UI can tell the user.
 */
export function resolveScanFeedback() {
  const saved = getScanFeedback();
  const opts = availableFeedbackOptions();
  if (opts.includes(saved)) return { mode: saved, changed: false, from: null };
  const fallback = 'beep'; // always supported (audio degrades silently if blocked)
  setScanFeedback(fallback);
  return { mode: fallback, changed: true, from: saved };
}

// --- audio ----------------------------------------------------------------
let _actx = null;
let _audioUnlocked = false;

function ensureCtx() {
  if (!isAudioSupported()) return null;
  if (!_actx) {
    try { _actx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch (e) { _actx = null; }
  }
  return _actx;
}

/**
 * Unlock audio + warm speech. MUST be called from inside a real user gesture
 * (tap) so iOS Safari lets later beeps/voice play. Plays NO audible sound.
 */
export function unlockAudio() {
  const ctx = ensureCtx();
  if (ctx) {
    try {
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      // A 1-sample silent buffer fully unlocks the context on iOS.
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      _audioUnlocked = true;
    } catch (e) { /* audio init failed — degrade silently */ }
  }
  // Prime the speech voice list (loads async; benefits from a gesture on iOS).
  try { if (isSpeechSupported()) window.speechSynthesis.getVoices(); } catch (e) { /* ignore */ }
}

function beep() {
  const ctx = ensureCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.16);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.17);
  } catch (e) { /* audio unavailable / blocked */ }
}

// --- voice ----------------------------------------------------------------
function pickVoice() {
  const vs = (isSpeechSupported() && window.speechSynthesis.getVoices()) || [];
  return vs.find((x) => /en-?IN/i.test(x.lang) && /female|woman|heera|kajal|neerja|swara|aditi|priya/i.test(x.name))
    || vs.find((x) => /en-?IN/i.test(x.lang))
    || vs.find((x) => /female|woman|google uk english female|samantha/i.test(x.name))
    || vs.find((x) => /^en/i.test(x.lang))
    || null; // no English voice -> let the platform choose its default
}
function speak() {
  if (!isSpeechSupported()) { beep(); return; }
  try {
    const u = new SpeechSynthesisUtterance('Item scanned');
    u.lang = 'en-IN'; u.rate = 1.05; u.pitch = 1.15; u.volume = 1;
    const v = pickVoice();
    if (v) u.voice = v; // otherwise the device default voice is used
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (e) { beep(); }
}

// Back-compat: warm voices (now also covered by unlockAudio()).
export function warmupVoices() {
  try { if (isSpeechSupported()) window.speechSynthesis.getVoices(); } catch (e) { /* ignore */ }
}

export function playScanFeedback() {
  const pref = getScanFeedback();
  if (pref === 'off') return;
  const buzz = () => { if (isVibrateSupported()) { try { navigator.vibrate(40); } catch (e) { /* ignore */ } } };
  if (pref === 'vibrate') { buzz(); return; }
  // 'beep' and 'voice' also give a short buzz for tactile confirmation (where supported).
  buzz();
  if (pref === 'voice') { speak(); return; }
  beep();
}
